import * as React from "react";

const SMALL_PHONE_BREAKPOINT = 375;
const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener("change", onChange);
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return !!isMobile;
}

export function useIsSmallPhone() {
  const [isSmallPhone, setIsSmallPhone] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    const onChange = () => {
      setIsSmallPhone(window.innerWidth < SMALL_PHONE_BREAKPOINT);
    };
    window.addEventListener("resize", onChange);
    onChange();
    return () => window.removeEventListener("resize", onChange);
  }, []);

  return !!isSmallPhone;
}

export function useIsTablet() {
  const [isTablet, setIsTablet] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    const onChange = () => {
      const width = window.innerWidth;
      setIsTablet(width >= MOBILE_BREAKPOINT && width < TABLET_BREAKPOINT);
    };
    window.addEventListener("resize", onChange);
    onChange();
    return () => window.removeEventListener("resize", onChange);
  }, []);

  return !!isTablet;
}

export function useViewportSize() {
  const [size, setSize] = React.useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  });

  React.useEffect(() => {
    const onChange = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onChange);
    return () => window.removeEventListener("resize", onChange);
  }, []);

  return size;
}

export function useIsPortrait() {
  const { width, height } = useViewportSize();
  // Consider portrait if height is significantly taller than width (aspect ratio > 1.2)
  // and width is less than a comfortable side-panel threshold
  return height > width * 1.2 && width < 900;
}

export function useViewportWidth() {
  const { width } = useViewportSize();
  return width;
}

export function useDeviceSize() {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isSmallPhone = useIsSmallPhone();
  const isPortrait = useIsPortrait();
  const viewportWidth = useViewportWidth();
  
  return {
    isMobile,
    isTablet,
    isSmallPhone,
    isPortrait,
    isDesktop: !isMobile && !isTablet,
    viewportWidth,
  };
}
